import { DomHelpers } from '../DomHelpers.js';
import { setMainValue, getMainValue } from '../EncodeValues.js';
import { ImViewBridge } from '../../legacy/ImViewBridge.js';
import { DefaultUiCommand } from './DefaultUiCommand.js';
import { parseSearchMask, findBySearchMask, findFirstBySearchMask } from './SearchMask.js';
import { DEFAULT_ID, DEFAULT_NS, executeOnItems } from './types.js';
import { AsyncWidget } from '../../widgets/async/AsyncWidget.js';
import { ImSys } from '../ImSys.js';
import { PageCloseController } from '../PageCloseController.js';
import { ViewWidget } from '../../view/ViewWidget.js';
import { utils } from '../../tools/Utils.js';
import { Loc } from '../../locales/registry.js';
import { DialogComponent } from '../../components/DialogComponent/DialogComponent.js';
import { TabsPanelWidget } from '../../widgets/TabsPanelWidget.js';
import { confirmAsideReplace } from '../tools/ModalAside.js';

// src/core/imcmd/DefaultUiCommandProvider.ts
/**
 * Системный провайдер для команд без префикса (универсальные DOM-операции).
 * Работает только с namespace = "" (отсутствует префикс у команды).
 */
class DefaultUiCommandProvider {
    constructor() {
        /** Идентификатор провайдера */
        this.id = DEFAULT_ID;
        /** Пространство имен провайдера */
        this.namespace = DEFAULT_NS;
    }
    /**
     * Внутренний селектор: ищет элементы в пределах текущего view.
     * Возвращает пустой массив, если селектор не задан или некорректен.
     */
    q(host, selector) {
        if (!selector)
            return [];
        try {
            return Array.from(host.querySelectorAll(selector));
        }
        catch {
            return [];
        } // на случай некорректного селектора
    }
    /**
     * Выполнение системной команды.
     */
    execute(token, ctx) {
        // принимаем только команды без namespace
        if (token.namespace !== DEFAULT_NS)
            return false;
        const cmd = token.command.toLowerCase();
        // Если переданы уже разобранные параметры отбора элементов, то 
        if (token.targetItems && token.targetItems.length > 0) {
            // Обработка переданных элементов            
            const result = executeOnItems(token, ctx, (el, token, ctx) => {
                switch (cmd) {
                    // case writeHtml и параметры брать строго из solvedArgs
                    // === МАНИПУЛЯЦИИ С DOM ===========================================================
                    case DefaultUiCommand.append:
                    case DefaultUiCommand.prepend:
                    case DefaultUiCommand.after:
                    case DefaultUiCommand.replace:
                    case DefaultUiCommand.replaceInner:
                        {
                            const solvedArgs = token.solvedArgs;
                            if (solvedArgs?.html) {
                                const fragment = document.createRange().createContextualFragment(solvedArgs.html);
                                const nodes = Array.from(fragment.childNodes);
                                if (cmd === DefaultUiCommand.append) {
                                    el.append(...nodes);
                                }
                                if (cmd === DefaultUiCommand.prepend) {
                                    el.prepend(...nodes);
                                }
                                if (cmd === DefaultUiCommand.after) {
                                    el.after(...nodes);
                                }
                                if (cmd === DefaultUiCommand.replace) {
                                    el.after(...nodes);
                                    el.remove();
                                }
                                if (cmd === DefaultUiCommand.replaceInner) {
                                    el.innerHTML = '';
                                    el.append(...nodes);
                                }
                                // распаковываем в элементы
                                const htmlElems = Array.from(nodes).filter((n) => n instanceof HTMLElement);
                                ImSys.initDomMany(htmlElems);
                                if (solvedArgs.show === true) {
                                    // Если есть скорость — можно сделать анимацию через CSS
                                    if (solvedArgs.speed) {
                                        const speed = Number(solvedArgs.speed);
                                        if (!isNaN(speed) && speed > 0) {
                                            DomHelpers.show(el, { duration: speed });
                                        }
                                        else {
                                            DomHelpers.show(el);
                                        }
                                    }
                                    else {
                                        // Просто показываем элемент
                                        DomHelpers.show(el);
                                    }
                                }
                                if (solvedArgs.sendevent === true) {
                                    const event = new Event('change', { bubbles: true });
                                    el.dispatchEvent(event);
                                }
                                return true;
                            }
                            return false;
                        }
                    // Алерт
                    case DefaultUiCommand.alert: {
                        const cmd = token.solvedArgs;
                        const popupPlace = el.querySelector(".im-popup-place");
                        if (popupPlace) {
                            popupPlace.innerHTML = "";
                            const note = document.createElement("div");
                            note.className = `alert-note ${cmd.icon ?? ""}`;
                            note.innerHTML = cmd.message ?? "";
                            popupPlace.appendChild(note);
                        }
                        else {
                            ImSys.alert(cmd.title ?? "", cmd.message, undefined, cmd.icon, cmd.css);
                        }
                        return true;
                    }
                    // === ПРОЦЕССЫ ============================================================
                    // newWatchProcess|<jsonArgs>
                    case DefaultUiCommand.showProcess: {
                        try {
                            // Аргументы можно передавать JSON-строкой в rawArgs[0], 
                            // или через solvedArgs (если Runner подставил).
                            let cmd = token.solvedArgs;
                            let proc;
                            const asyncContainer = document.createElement("div");
                            asyncContainer.className = "im-async" + (cmd.css ? " " + cmd.css : "");
                            asyncContainer.dataset.proc = cmd.process;
                            const options = {
                                proc: cmd.proc,
                                owner: ViewWidget.closestView(el)?.element ?? ctx.viewHost,
                                updateInterval: cmd.updateInterval,
                                initText: cmd.initText,
                                contentHtml: cmd.contentHtml,
                                statusPosition: cmd.satusPosition,
                                cbi: cmd.cbi,
                                mode: cmd.mode,
                                modal: cmd.modal
                            };
                            proc = AsyncWidget.getOrInit(asyncContainer, options);
                            if (cmd.modal === true) {
                                ImSys.modalProgress(asyncContainer, () => proc.cancelProc());
                            }
                            else {
                                const targetRoot = el;
                                if (cmd.add !== "true" && targetRoot) {
                                    while (targetRoot.firstChild) {
                                        targetRoot.removeChild(targetRoot.firstChild);
                                    }
                                }
                                let exist = false;
                                if (targetRoot) {
                                    const existing = targetRoot.querySelectorAll('.im-async');
                                    existing.forEach((node) => {
                                        const existingAsync = AsyncWidget.from(node);
                                        if (existingAsync && typeof existingAsync.procID === 'function') {
                                            if (existingAsync.procID() === cmd.process) {
                                                exist = true;
                                            }
                                        }
                                    });
                                }
                                if (!exist) {
                                    proc = AsyncWidget.getOrInit(asyncContainer, options);
                                    if (!targetRoot) {
                                        ImSys.modalProgress(proc.element, () => proc.cancelProc());
                                    }
                                    else {
                                        targetRoot.appendChild(proc.element ?? asyncContainer);
                                    }
                                }
                            }
                            return true;
                        }
                        catch (e) {
                            console.error("[newWatchProcess] error:", e);
                            return false;
                        }
                    }
                    //Вставка разметки
                    case DefaultUiCommand.openInPlace: {
                        const cmd = token.solvedArgs;
                        const place = el;
                        if (place) {
                            if (!cmd.append && !cmd.prepend) {
                                place.replaceChildren();
                            }
                            const temp = document.createElement("div");
                            temp.innerHTML = cmd.html ?? "";
                            const newEl = Array.from(temp.childNodes);
                            if (cmd.prepend) {
                                place.prepend(...newEl);
                            }
                            else {
                                place.append(...newEl);
                            }
                            ImSys.initDom(place);
                        }
                        return true;
                    }
                    case DefaultUiCommand.openAside: {
                        const cmd = token.solvedArgs;
                        let aside = document.querySelector(".im-modal-aside:not(.im-modal-aside-filter)");
                        if (aside) {
                            const target = aside;
                            // 2026-09-14: замена формы с несохранёнными данными — только после подтверждения
                            confirmAsideReplace(() => {
                                target.querySelector(".modal-aside__title").innerHTML = cmd.title ?? "";
                                target.querySelector(".im-view")?.remove();
                                target.querySelector(".modal-aside__content").innerHTML = cmd.html ?? "";
                                ImSys.initDom(target);
                                DomHelpers.show(target);
                            });
                            return true;
                        }
                        else {
                            aside = document.createElement("aside");
                            aside.className = "im-modal-aside modal-aside";
                            aside.innerHTML = `
                                <div class="modal-aside__header">
                                    <h4 class="modal-aside__title">${cmd.title ?? ""}</h4>
                                    <i class="modal-aside__close"></i>
                                </div>
                                <div class="modal-aside__content">${cmd.html ?? ""}</div>
                            `;
                            document.body.appendChild(aside);
                            ImSys.initDom(aside);
                            return true;
                        }
                    }
                    // Открытие формы
                    case DefaultUiCommand.openForm: {
                        const cmd = token.solvedArgs;
                        let panel = ctx.viewHost.closest(".im-panel");
                        if (!panel)
                            panel = document.body.querySelector('.im-panel');
                        if (cmd.modal === true) {
                            const instance = DialogComponent.show({
                                title: cmd.title,
                                content: cmd.html,
                                width: cmd.width,
                                height: cmd.height,
                                dialogClass: cmd.css,
                                modal: true
                            });
                            ImSys.initDom(instance);
                            return true;
                        }
                        else if (panel) {
                            TabsPanelWidget.getOrInit(panel).addTab({
                                title: cmd.title,
                                html: cmd.html,
                                description: "",
                                viewChanges: true,
                            });
                            return true;
                        }
                        else {
                            const instance = DialogComponent.show({
                                title: cmd.title,
                                content: cmd.html,
                                width: cmd.width,
                                height: cmd.height,
                                dialogClass: cmd.css,
                                modal: false
                            });
                            ImSys.initDom(instance);
                            return true;
                        }
                    }
                    /** Реализация для серверной команды */
                    case DefaultUiCommand.event: {
                        const cmd = token.solvedArgs;
                        if (cmd.eventKey) {
                            ImViewBridge.execEvent(el, cmd.eventKey, cmd.options);
                            return true;
                        }
                        else
                            return false;
                    }
                    default:
                        return false;
                }
            });
            if (result) {
                return true;
            }
        }
        // Инлайновые команды поданные как строка c rawArgs например addClass|<selector>|<class>, для этих команд подразумевается что селектор идет 1м
        switch (cmd) {
            // === КЛАССЫ ==============================================================
            // addClass|<selector>|<class>
            case DefaultUiCommand.addClass:
            // removeClass|<selector>|<class>
            case DefaultUiCommand.removeClass:
            // toggleClass|<selector>|<class>
            case DefaultUiCommand.toggleClass:
            // tempClass|<selector>|<class>|<delay>
            case DefaultUiCommand.tempClass:
                {
                    const [rawSelector, cls, rawDelay] = token.rawArgs;
                    if (!rawSelector || !cls)
                        return true;
                    // используем SearchMask для поддержки @key и зон ^v/^i/^g/^c/^p/^pi
                    const sm = parseSearchMask(rawSelector /* tableMode = false */);
                    const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                    nodes.forEach(el => {
                        if (cmd === DefaultUiCommand.addClass)
                            DomHelpers.addClass(el, cls);
                        else if (cmd === DefaultUiCommand.removeClass)
                            DomHelpers.removeClass(el, cls);
                        else if (cmd === DefaultUiCommand.tempClass) {
                            const delay = rawDelay ? Number(rawDelay) : 1600;
                            DomHelpers.addTmpClass(el, delay, cls);
                        }
                        else
                            el.classList.toggle(cls);
                    });
                    return true;
                }
            // === ВИДИМОСТЬ ===========================================================
            // show|<selector>|<speed>     — убрать видимость
            // hide|<selector>|<speed>     — поставить видимость
            // toggle|<selector>|<speed>   — переключить видимость
            case DefaultUiCommand.show:
            case DefaultUiCommand.hide:
            case DefaultUiCommand.toggle:
                {
                    const [rawSelector, speedRaw] = token.rawArgs;
                    if (!rawSelector)
                        return true;
                    // Скорость скрытия/раскрытия
                    const speed = Number(speedRaw ?? 0);
                    const sm = parseSearchMask(rawSelector /* tableMode = false */);
                    const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                    // TODO: Добавить параметр
                    nodes.forEach((el) => {
                        if (cmd === DefaultUiCommand.show) {
                            DomHelpers.isHidden(el) && DomHelpers.show(el, { duration: speed });
                        }
                        else if (cmd === DefaultUiCommand.hide) {
                            DomHelpers.hide(el, { duration: speed });
                        }
                        else {
                            if (DomHelpers.isHidden(el))
                                DomHelpers.show(el, { duration: speed });
                            else
                                DomHelpers.hide(el, { duration: speed });
                        }
                    });
                    return true;
                }
            case DefaultUiCommand.remove: {
                const [rawSelector] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector);
                const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                nodes.forEach((el) => el.remove());
                return true;
            }
            // === ФОКУС ===============================================================
            // focus|<selector> — фокус на найденных элементах
            case DefaultUiCommand.focus: {
                const [rawSelector] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector /* tableMode = false */);
                const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                nodes.forEach((el) => el.focus());
                return true;
            }
            // === СОБЫТИЯ/КЛИПБОРД/НАВИГАЦИЯ =========================================
            // event|<name> — системное событие (совместимость с legacy imsys.e.execEvent)
            /** Реализация для команды в imcmd */
            case DefaultUiCommand.event: {
                const [evtName] = token.rawArgs;
                if (evtName) {
                    const btn = ctx.sourceEl;
                    const tableEl = btn?.closest?.('.im-table');
                    // как в легаси: если внутри таблицы — table-cmd, иначе view-cmd
                    const options = tableEl
                        ? { cmdType: 'table-cmd', table: tableEl, button: btn }
                        : { cmdType: 'view-cmd', button: btn };
                    ImViewBridge.execEvent(btn ?? null, evtName, options);
                }
                return true;
            }
            // copytoclip|<selector> — скопировать текст/значение первого найденного элемента в буфер
            case DefaultUiCommand.copyToClip: {
                const [rawSelector] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector /* tableMode = false */);
                const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                const el = nodes[0];
                if (!el)
                    return true;
                const text = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
                    ? el.value
                    : (el.textContent ?? "");
                void utils.copyToClipboard(text);
                ImViewBridge.execEvent(ctx.sourceEl, 'show-tips', { msg: Loc.copiedToBuffer });
                return true;
            }
            // redirect|<url> — перенаправить браузер на указанный адрес
            case DefaultUiCommand.redirect: {
                const [url, newTab] = token.rawArgs;
                // 2026-08-25: переход выполняет само приложение по ответу сервера,
                // поэтому предупреждение о несохранённых данных снимается. Для
                // новой вкладки текущая страница остаётся, страж не трогается.
                if (!url) {
                    PageCloseController.suppress();
                    window.location.reload();
                    return true;
                }
                if (newTab === 'true') {
                    window.open(url, '_blank');
                }
                else {
                    PageCloseController.suppress();
                    window.location.href = url;
                }
                return true;
            }
            // === ЗНАЧЕНИЯ ============================================================
            // setval|<selector>|<value>|<fireChanges>|<title> — установить значение
            case DefaultUiCommand.setval: {
                const [rawSelector, value, maybeFlag, title] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector /* tableMode = false */);
                const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                const flagLower = maybeFlag?.toLowerCase();
                const fireChange = flagLower === 'change' || flagLower == "true";
                nodes.forEach((el) => {
                    // TODO Для комобокса не работает, точнее не передается title для combobox widget, чтобы его можно было использовать для показа
                    setMainValue(el, value ?? '');
                    if (title) {
                        // todo: костыль установки  data-title
                        // todo: потом перевести на setMainValues а не одиночный
                        el.dataset.title = title;
                    }
                    if (fireChange) {
                        el.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
                return true;
            }
            // setvalfrom|<fromSelector>|<toSelector>
            // копирует значение из from → to
            case DefaultUiCommand.setvalFrom: {
                const [fromSel, toSel] = token.rawArgs;
                if (!fromSel || !toSel)
                    return true;
                const fromSm = parseSearchMask(fromSel /* tableMode = false */);
                const toSm = parseSearchMask(toSel /* tableMode = false */);
                const fromNodes = findBySearchMask(ctx.viewHost, fromSm, ctx.sourceEl, ctx.uiNotMask);
                const toNodes = findBySearchMask(ctx.viewHost, toSm, ctx.sourceEl, ctx.uiNotMask);
                const from = fromNodes[0];
                if (!from)
                    return true;
                const v = getMainValue(from);
                if (v === undefined)
                    return true;
                toNodes.forEach((el) => setMainValue(el, v));
                return true;
            }
            // clr|<selector> — очистить значение(я)
            case DefaultUiCommand.clr: {
                const [rawSelector] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector /* tableMode = false */);
                const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                nodes.forEach((el) => setMainValue(el, ''));
                return true;
            }
            /** Закрыть всплывающее окно (popup) внутри которого view */
            case DefaultUiCommand.closePopup: {
                const aside = ctx.viewHost.closest('.im-modal-aside');
                if (aside) {
                    // Если в асайде, то закрываем через кнопку закрытия асайда
                    const btn = aside.querySelector('.modal-aside__close');
                    if (btn) {
                        btn.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                        return true;
                    }
                }
                else {
                    // Если в диалоге - то просто вызываем событие для закрытия
                    ctx.viewHost.dispatchEvent(new CustomEvent('im-dialog:close', {
                        bubbles: true,
                        composed: true
                    }));
                    return true;
                }
                return false;
            }
            case DefaultUiCommand.setAttribute: {
                const [rawSelector, attr, value] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector);
                const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                nodes.forEach((el) => {
                    el.setAttribute(attr, value);
                });
                return true;
            }
            // NOTE: Надо добавить возможность прокрутки для элементов, подумать над передаваемым параметром
            // TODO
            case DefaultUiCommand.scrollTo: {
                const [rawSelector] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector /* tableMode = false */);
                if (sm.mask === 'top')
                    DomHelpers.scrollTo(window, sm.mask);
                else {
                    const elTarget = findFirstBySearchMask(ctx.viewHost, sm);
                    if (elTarget) {
                        DomHelpers.scrollTo('auto', elTarget);
                    }
                    else
                        DomHelpers.scrollTo('auto', sm.mask);
                }
                return true;
            }
            case DefaultUiCommand.activeTab: {
                const [rawSelector, tabindex] = token.rawArgs;
                if (!rawSelector)
                    return true;
                const sm = parseSearchMask(rawSelector);
                const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                nodes.forEach((el) => setMainValue(el, tabindex));
                return true;
            }
            case DefaultUiCommand.htmlMoveDown:
                {
                    const [rawSelector] = token.rawArgs;
                    if (!rawSelector)
                        return true;
                    // Парсим селектор с поддержкой масок
                    const sm = parseSearchMask(rawSelector);
                    const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                    // Для каждого найденного элемента перемещаем ниже (после следующего соседа)
                    nodes.forEach((el) => {
                        const next = el.nextElementSibling;
                        if (next)
                            next.after(el);
                    });
                    return true;
                }
            case DefaultUiCommand.htmlMoveUp:
                {
                    const [rawSelector] = token.rawArgs;
                    if (!rawSelector)
                        return true;
                    // Парсим селектор с поддержкой масок
                    const sm = parseSearchMask(rawSelector);
                    const nodes = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask);
                    // Для каждого найденного элемента перемещаем выше (до предыдущего соседа)
                    nodes.forEach((el) => {
                        const prev = el.previousElementSibling;
                        if (prev)
                            prev.before(el);
                    });
                    return true;
                }
        }
        // команда не поддерживается системным провайдером
        return false;
    }
}

export { DefaultUiCommandProvider };
